import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  Input,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormsModule,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { marker as _ } from "@biesbjerg/ngx-translate-extract-marker";
import { CatalogModule, ProductDetailService } from "@bb-vendure/admin-ui/catalog";
import GenerateProductVariantsComponent from "./components/generate-product-variants/generate-product-variants.component";
import {
  Asset,
  CreateProductInput,
  CustomFieldConfig,
  CustomFields,
  DataService,
  Dialog,
  GetProductDetailQuery,
  GetProductWithVariantsQuery,
  LanguageCode,
  NotificationService,
  Permission,
  ProductDetailFragment,
  ServerConfigService,
  SharedModule,
  UpdateProductInput,
  createUpdatedTranslatable,
  findTranslation,
  getCustomFieldsDefaults,
  unicodePatternValidator,
} from "@bb-vendure/admin-ui/core";
import { normalizeString } from "@vendure/common/lib/normalize-string";
import {
  Observable,
  distinctUntilChanged,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
  Subject,
  takeUntil,
} from "rxjs";
import {
  GetRetailerDocument,
  GetArtistDocument,
} from "../../generated-graphql/graphql";

interface SelectedAssets {
  assets?: Asset[];
  featuredAsset?: Asset;
}
export type CreateVariantValues = {
  optionValues: string[];
  enabled: boolean;
  sku: string;
  price: number;
  stock: number;
  supplier: string;
};
export type CreateProductVariantsConfig = {
  groups: Array<{
    name: string;
    values: string[];
  }>;
  variants: CreateVariantValues[];
  stockLocationId: string;
};

@Component({
  standalone: true,
  templateUrl: "./product-create.component.html",
  imports: [
    SharedModule,
    CatalogModule,
    FormsModule,
    GenerateProductVariantsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class ProductCreateDialogComponent
  implements Dialog, OnInit, OnDestroy
{
  constructor(
    private formBuilder: FormBuilder,
    protected notificationService: NotificationService,
    protected serverConfigService: ServerConfigService,
    protected dataService: DataService,
    protected route: ActivatedRoute,
    protected productDetailService: ProductDetailService
  ) {}
  @Input() retailer: string;

  @Input() artist: string;

  @Input() name: string;

  @Input() parentForm: UntypedFormGroup;

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private destroy$ = new Subject<void>();

  readonly customFields = this.getCustomFieldConfig("Product");
  protected getCustomFieldConfig(
    key: Exclude<keyof CustomFields, "__typename">
  ): CustomFieldConfig[] {
    return this.serverConfigService.getCustomFieldsFor(key).filter((f) => {
      // if (f.requiresPermission?.length) {
      //     return this.permissionsService.userHasPermissions(f.requiresPermission);
      // }
      return true;
    });
  }
  noFreeGiftCustomFields = this.getCustomFieldConfig("Product");

  getCustomFieldNoGifts() {
    var result = this.getCustomFieldConfig("Product");

    const excludeCustomFields = ["freeGifts"];

    if (this.detailForm.get(["customFields", "freeGifts"])?.value) {
      excludeCustomFields.push("retailer");
      excludeCustomFields.push("artist");
      excludeCustomFields.push("supplier");
      // excludeCustomFields.push("shippingSetting");
      excludeCustomFields.push("splitOrderRule");
      excludeCustomFields.push("defaultProductDescription");
    }

    result = result.filter((f) => !excludeCustomFields.includes(f.name));

    this.noFreeGiftCustomFields = result;
    //this.changeDetector.markForCheck();

    return result;
  }

  resolveWith: (result?: any) => void;

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((paramMap) => paramMap.get("lang")),
        switchMap((lang) => {
          if (lang) {
            return of(lang as LanguageCode);
          } else {
            return this.dataService.client
              .uiState()
              .mapSingle((data) => data.uiState.contentLanguage);
          }
        }),
        distinctUntilChanged(),
        tap((val) => (this.languageCode = val)),
        shareReplay(1),
        takeUntil(this.destroy$)
      )
      .subscribe((lang) => (this.languageCode = lang));

    // this.detailForm.get('customFields.freeGifts')?.setValue(true);

    //@ts-ignore
    // this.detailForm.get("customFields.shippingSetting")!.patchValue(this.parentForm.get("customfields.shippingSetting")!.value);
      const val = this.parentForm.get("customFields.shippingSetting")?.value;
    if (val) {
      this.detailForm.get("customFields.shippingSetting")!.patchValue(val as never); 
    } else {
      this.detailForm.get("customFields.shippingSetting")!.patchValue("Quantity" as never);
    }
    this.detailForm.get("customFields.shippingSetting")!.valueChanges.subscribe(console.log)

    if (this.name) {
      this.detailForm.patchValue({
        name: `${this.name} ${Math.floor(Math.random() * 90000) + 10000}`,
      });

      this.updateSlug(this.detailForm.value.name!);
    }
    this.detailForm
      .get("customFields.freeGifts")
      ?.valueChanges.subscribe((r) => {
        console.log("ss", r);
      });

    this.dataService
      .query(GetRetailerDocument, {
        id: this.retailer,
      })
      .single$.pipe()
      .subscribe((res) => {
        (
          this.detailForm.controls.customFields.controls as any
        ).retailer.patchValue(res.retail);
      });

    this.dataService
      .query(GetArtistDocument, {
        id: this.artist,
      })
      .single$.pipe()
      .subscribe((res) => {
        (
          this.detailForm.controls.customFields.controls as any
        ).artist.patchValue(res.artist);
      });

    this.detailForm.get(["customFields", "customThreshold"])?.patchValue(990);

    this.getCustomFieldNoGifts();
  }

  getCustomField() {
    let fields = getCustomFieldsDefaults(this.customFields);
    return {
      ...fields,
      freeGifts: true,
      createdInJackyun: true,
      startTime: new Date().toISOString(),
      endTime: new Date().toISOString(),
    };
  }

  detailForm = this.formBuilder.group({
    enabled: true,
    name: ["", Validators.required],
    autoUpdateVariantNames: true,
    slug: ["", unicodePatternValidator(/^[\p{Letter}0-9._-]+$/)],
    description: "",
    facetValueIds: [[] as string[]],
    customFields: this.formBuilder.group(this.getCustomField()),
  });
  formControl: FormControl<any>;
  assetChanges: SelectedAssets = {};
  productChannels$: Observable<ProductDetailFragment["channels"]>;
  facetValues$: Observable<ProductDetailFragment["facetValues"]>;
  createVariantsConfig: CreateProductVariantsConfig = {
    groups: [],
    variants: [],
    stockLocationId: "",
  };
  languageCode: any;
  public readonly updatePermissions = [
    Permission.UpdateCatalog,
    Permission.UpdateProduct,
  ];

  title = "common.create";

  buttons = [{ label: "Close", type: "primary", click: () => this.create() }];

  updateSlug(nameValue: string) {
    // const slugControl = this.detailForm.get("slug");
    // const currentTranslation = undefined;
    // const currentSlugIsEmpty = !currentTranslation;
    // if (slugControl && slugControl.pristine && currentSlugIsEmpty) {
    //   slugControl.setValue(normalizeString(`${nameValue}`, "-"));
    // }
    nameValue = nameValue.replace(/[^\u0000-\u007F]/g, "");
    const slugControl = this.detailForm.get("slug");
    const currentTranslation = undefined;
    const currentSlugIsEmpty = !currentTranslation;
    if (slugControl && slugControl.pristine && currentSlugIsEmpty) {
      slugControl.setValue(normalizeString(`${nameValue}`, "-"));
    }
  }

  protected setFormValues(
    product: NonNullable<GetProductWithVariantsQuery["product"]>,
    languageCode: LanguageCode
  ) {
    const currentTranslation = findTranslation(product, languageCode);
    this.detailForm.patchValue({
      enabled: product.enabled,
      name: currentTranslation ? currentTranslation.name : "",
      slug: currentTranslation ? currentTranslation.slug : "",
      description: currentTranslation ? currentTranslation.description : "",
      facetValueIds: product.facetValues.map((fv) => fv.id),
    });
  }

  private getUpdatedProduct(
    product: NonNullable<GetProductDetailQuery["product"]>,
    productFormGroup: UntypedFormGroup,
    languageCode: LanguageCode
  ): UpdateProductInput | CreateProductInput {
    const updatedProduct = createUpdatedTranslatable({
      translatable: product,
      updatedFields: productFormGroup.value,
      customFieldConfig: this.customFields,
      languageCode,
      defaultTranslation: {
        languageCode,
        name: product.name || "",
        slug: product.slug || "",
        description: product.description || "",
      },
    });
    return {
      ...updatedProduct,
      assetIds: this.assetChanges.assets?.map((a) => a.id),
      featuredAssetId: this.assetChanges.featuredAsset?.id,
      facetValueIds: productFormGroup.value.facetValueIds,
    } as UpdateProductInput | CreateProductInput;
  }

  create() {
    (this.detailForm.controls.customFields.controls as any).status.patchValue(
      "Approved"
    );

    const productGroup = this.detailForm;

    if (!this.checkFreeGiftStock()) {
      return;
    }

    const newProduct = this.getUpdatedProduct(
      {
        id: "",
        createdAt: "",
        updatedAt: "",
        enabled: true,
        languageCode: this.languageCode,
        name: "",
        slug: "",
        featuredAsset: null,
        assets: [],
        description: "",
        translations: [],
        optionGroups: [],
        facetValues: [],
        channels: [],
      },
      productGroup as UntypedFormGroup,
      this.languageCode
    ) as CreateProductInput;
    this.productDetailService
      .createProductWithVariants(
        newProduct,
        this.createVariantsConfig,
        this.languageCode
      )
      .subscribe(({ createProductVariants, productId }) => {
        const outOfStockThreshold = this.detailForm.get([
          "customFields",
          "customThreshold",
        ])?.value;
        const payload = createProductVariants
          .filter((v) => v !== null)
          .map((v) => {
            let weight = 500;
            if (this.detailForm.get("customFields.freeGifts")!.value) {
              weight = 0; 
            }
            return {
              id: v!.id,
              outOfStockThreshold: -outOfStockThreshold,
              useGlobalOutOfStockThreshold: outOfStockThreshold === 0,
              customFields: {
                weight
              }
            };
          });

        this.dataService.product
          .updateProductVariants(payload)
          .subscribe((v) => {
            console.log("updateProductVariants", v);
          });
        this.notificationService.success(_("common.notify-create-success"), {
          entity: "Product",
        });
        this.assetChanges = {};
        this.detailForm.markAsPristine();
        this.resolveWith(productId);
        //this.close();
      });
  }

  close() {
    this.resolveWith();
  }

  addRetailer() {
    this.dataService
      .query(GetRetailerDocument, {
        id: this.retailer,
      })
      .single$.pipe()
      .subscribe((res) => {
        (
          this.detailForm.controls.customFields.controls as any
        ).retailer.patchValue(res.retail);

        this.create();
      });
  }

  test() {
    console.log(this.detailForm);
  }

  checkFreeGiftStock() {
    const isFreeGift = this.detailForm.controls.customFields.value.freeGifts;
    const preStock = (this.detailForm.controls.customFields.value as any)
      .customThreshold;

    if (!isFreeGift) {
      return true;
    }

    if (preStock <= 0) {
      for (const v of this.createVariantsConfig.variants) {
        if (v.stock <= 0) {
          this.notificationService.error(
            "The free gift quantity (including pre-orders) is 0"
          );
          return false;
        }
      }
    }

    return true;
  }
}
